import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';

import { ProductMeta } from 'ideta-library/lib/common/bot';
import { Page } from 'ideta-library/lib/common/channels/meta';

import { AutoReplyService } from '../../services/auto-reply/auto-reply.service';
import { ErrorsService } from '../../services/errors/errors.service';
import { FeedbackService } from '../../services/feedback/feedback.service';
import { MetaService } from '../../services/meta/meta.service';
import { UserSessionService } from '../../services/session/user-session.service';
import { ToasterService } from '../../services/toaster/toaster.service';

export type PageMode = 'select' | 'manage';

export type DisplayPage = Partial<Page> & { channel: ProductMeta; connected?: boolean; linkId?: string }; // Change the auto-reply.cleanPAge method when modifying this type

@Component({
  selector: 'app-pages-manager',
  templateUrl: './pages-manager.component.html',
  styleUrls: ['./pages-manager.component.scss']
})
export class PagesManagerComponent implements OnInit {
  @Input() mode: PageMode = 'select';
  @Output() selection: EventEmitter<DisplayPage> = new EventEmitter();

  isLoaded: boolean;
  listFBPages: DisplayPage[] = [];
  listInstagramPages: DisplayPage[] = [];
  selectedPage: string;
  fbAddPage: DisplayPage;
  instaAddPage: DisplayPage;

  private licenceCustomer: string;
  private licenceCustomerDetails: any;
  private subscriptions: Subscription[];

  constructor(
    private _user: UserSessionService,
    private autoReplyService: AutoReplyService,
    private metaService: MetaService,
    private feedbackService: FeedbackService,
    private errorsService: ErrorsService,
    private toasterService: ToasterService
  ) {
    this.fbAddPage = { name: 'Add facebook pages', channel: 'facebook' };
    this.instaAddPage = { name: 'Add instagram accounts', channel: 'instagram' };
    this.selection = new EventEmitter();
    this.subscriptions = [];
  }

  ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([this._user.fbPagesSubject$, this._user.instagramPagesSubject$]).subscribe(([fbPages, instaPages]) => {
        if (this.mode === 'manage') {
          this.listFBPages = (fbPages || []).map(page => ({ ...page.pageInfos, channel: 'facebook', connected: true, linkId: page.linkId }));
        } else {
          // Some non connected pages may already be present in the list so we want to merge them
          (fbPages || []).forEach(page => {
            const existingPage = this.listFBPages.find(p => p.id === page.pageInfos.id);
            if (existingPage) {
              existingPage.connected = true;
              existingPage.linkId = page.linkId;
            } else this.listFBPages.push({ ...page.pageInfos, channel: 'facebook', connected: true, linkId: page.linkId });
          });
        }

        this.listInstagramPages = (instaPages || []).map(page => ({
          ...page.pageInfos,
          channel: 'instagram',
          connected: true,
          linkId: page.linkId
        }));
        this.isLoaded = true;
        setTimeout(() => this.preselectPage(), 500);
      }),
      this._user.licenceSubject$.subscribe(licence => {
        this.licenceCustomer = licence?.fullData?.data?.object?.customer;
        this.licenceCustomerDetails = licence?.fullData?.data?.object?.customer_details;
      })
    );
  }

  async getMore(channel: ProductMeta) {
    if (!this.listFBPages?.length && !this.listInstagramPages?.length) this.isLoaded = false; // displaying spinner again
    if (channel === 'facebook') {
      const pages: Page[] = await this.metaService.getPages();
      pages
        .filter(page => !this.listFBPages.find(p => p.id === page.id))
        .forEach(p => this.listFBPages.push({ ...p, channel: 'facebook', connected: false }));
    }
    if (channel === 'instagram') {
      this.metaService.connectToBusinessLogin(this._user.id);
    }
    this.preselectPage();
    this.isLoaded = true;
  }

  select(page: DisplayPage) {
    if (this.mode !== 'select' || page.id === this.selectedPage) return;
    this.selectedPage = page.id;
    this.selection.emit(page);
  }

  disconnect(page: DisplayPage, channel: ProductMeta) {
    if (this.mode !== 'manage') return;

    this.feedbackService
      .showMessage({
        message: 'autocomment.disconnect-page-confirm',
        title: 'autocomment.disconnect-page-confirm-title',
        type: 'confirm',
        decoration: 'warning',
        messageParams: {
          length: 1
        }
      })
      .then((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.feedbackService
            .showSpinner()
            .then(() => this.autoReplyService.disconnectPage(page.id, channel).catch(this.errorsService.handlePromise()))
            .then(() => this.feedbackService.hideSpinner());
        }
      });
  }

  canLink(page: DisplayPage): boolean {
    return this.mode === 'manage' && !page.linkId && !!this.licenceCustomer && !!this.licenceCustomerDetails;
  }

  trackById(_, page: DisplayPage): string {
    return page.id;
  }

  link(page: DisplayPage) {
    if (this.mode !== 'manage') return;
    this.feedbackService.showSpinner();

    this.autoReplyService.linkUpInbox(page, this.licenceCustomer, this.licenceCustomerDetails).then(res => {
      this.feedbackService.hideSpinner();

      if (!res.success) {
        if (res.error?.projects) {
          let info: string = ': </br>';
          for (let i = 0; i < res.error.projects.length; i++) {
            info += '.&nbspName: ' + res.error.projects[i].name + '</br>';
            if (!!res.error.projects[i].access) info += '&nbsp&nbspLink to Project: ' + res.error.projects[i].access + '</br></br>';
          }

          this.toasterService.showToast('info', {
            title: 'Info',
            message: `${res.error.message} ${info} `
          });
        } else {
          this.toasterService.showToast('error', {
            title: 'Error',
            message: res.error?.message || 'An error occurred during the linking process'
          });
        }
      } else {
        page.linkId = res.linkId;
        setTimeout(() => this._user.refreshPages(page.channel), 1000);
        this.toasterService.showToast('success', {
          title: 'Success',
          message: 'Inbox is now linked to your page. Get the link in the campaign list when a campaign on this page is created!'
        });
      }
    });
  }

  private preselectPage() {
    const unified = [...this.listFBPages, ...this.listInstagramPages];
    if (!!this.selectedPage && !unified.find(pageco => pageco.id === this.selectedPage)) this.selectedPage = null;
    if (!this.selectedPage && unified.length === 1) this.select(unified[0]);
  }

  // Partially validated --------------------------------------------------------------------------------

  // NOT VALIDATED ------------------------------------------------------------------------------------------

  // creditsInfos: any;

  // get customer() {
  //   return this.creditsInfos?.data?.object?.customer;
  // }

  // get customerDetails() {
  //   return this.creditsInfos?.data?.object?.customer_details;
  // }

  /* @Input() inline: boolean;
  @Output() goToConnectPage: EventEmitter<void>;

  channels: AvailableChannels[];

  
  showConnectPage = false;
  showGoToCreateBtn = false;

  

  customer: any;
  customer_details: any;
  hasLicence: boolean;
  linkStatus: string[];

  fbExpanded: boolean;
  instagramExpanded: boolean;

  constructor(
    private autoReplyService: AutoReplyService,
    private botApiService: BotApiService,
    private feedbackService: FeedbackService,
    private errorsService: ErrorsService,
    private toasterService: ToasterService
  ) {
    this.channels = ['page', 'instagram'];
    this.subscriptions = [];
    this.goToConnectPage = new EventEmitter();
  }
  

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  toggleShowConnectPage() {
    this.showConnectPage = !this.showConnectPage;
  }

  toggleShowGoToCreateBtn(page: any) {
    if (!!page) this.showGoToCreateBtn = true;
    else this.showGoToCreateBtn = false;
  }

  disconnectPage(pageID, channel) {
    this.feedbackService
      .showMessage({
        message: 'autocomment.disconnect-page-confirm',
        title: 'autocomment.disconnect-page-confirm-title',
        type: 'confirm',
        decoration: 'warning',
        messageParams: {
          length: 1
        }
      })
      .then((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.feedbackService
            .showSpinner()
            .then(() => this.botApiService.disconnectFacebookPage(pageID, channel).catch(this.errorsService.handlePromise()))
            .then(() => this.feedbackService.hideSpinner());
        }
      });
  }



  goToConnect() {
    this.goToConnectPage.emit();
  }

  linkUpInbox(channel, page) {
    page.spinningLink = true;
    this.autoReplyService
      .linkUpInbox(channel, page.pageInfos.id, this.customer, this.customer_details)
      .then(res => {
        page.spinningLink = false;
        if (!!res) {
          if (!res.status) {
            if (res.message) {
              this.toasterService.showToast('error', {
                title: 'Error',
                message: res.message
              });
            } else {
              let info = '';
              page.linkError = true;
              if (res.error.projects) {
                info = ': </br>';
                for (let i = 0; i < res.error.projects.length; i++) {
                  info += '.&nbspName: ' + res.error.projects[i].name + '</br>';
                  if (!!res.error.projects[i].access) info += '&nbsp&nbspLink to Project: ' + res.error.projects[i].access + '</br></br>';
                }
              }

              this.toasterService.showToast('info', {
                title: 'Info',
                message: `${res.error.message} ${info} `
              });
            }
          } else
            this.toasterService.showToast('success', {
              title: 'Success',
              message: 'Inbox is now linked to your page. Get the link in the campaign list when a campaign on this page is created!'
            });
        }
      })
      .catch(_ => {
        page.spinningLink = false;
      });
  }

  expandList(channel) {
    if (window.innerWidth < 576) {
      if (channel === 'facebook') {
        this.fbExpanded = !this.fbExpanded;
        this.instagramExpanded = false;
      } else {
        this.instagramExpanded = !this.instagramExpanded;
        this.fbExpanded = false;
      }
    } else channel === 'facebook' ? (this.fbExpanded = !this.fbExpanded) : (this.instagramExpanded = !this.instagramExpanded);
  } */
}
